import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft,
  ArrowRight,
  Pause,
  Play,
  X,
  CheckCircle2,
  Home,
  CalendarDays,
  DollarSign,
  Wrench,
  Megaphone,
  Shield,
  FileText,
  ScrollText,
} from "lucide-react";
import { TourIllustration } from "@/components/help/TourIllustration";

interface Step {
  icon: React.ReactNode;
  titleEs: string;
  titleEn: string;
  bodyEs: string;
  bodyEn: string;
  highlightsEs: string[];
  highlightsEn: string[];
  illustration: "welcome" | "governance" | "finance" | "resident" | "security" | "hr" | "legal" | "complete";
  accent: string;
}

const STEPS: Step[] = [
  {
    icon: <Home size={28} />,
    titleEs: "Bienvenido a Costa Dorada I",
    titleEn: "Welcome to Costa Dorada I",
    bodyEs:
      "Este portal centraliza la gestión del condominio: finanzas, gobierno, residentes, seguridad, personal y documentos legales. Su experiencia depende de su rol: Administrador, Junta o Titular.",
    bodyEn:
      "This portal centralizes condominium management: finance, governance, residents, security, staff, and legal documents. Your experience depends on your role: Administrator, Board, or Owner.",
    highlightsEs: [
      "Bilingüe (Español/Inglés) con cambio instantáneo",
      "Auditoría completa de cada acción",
      "Notificaciones automáticas por correo",
    ],
    highlightsEn: [
      "Bilingual (Spanish/English) with instant switching",
      "Full audit trail of every action",
      "Automatic email notifications",
    ],
    illustration: "welcome",
    accent: "from-primary-50 to-primary-100",
  },
  {
    icon: <CalendarDays size={28} />,
    titleEs: "Módulo 1 — Gobierno",
    titleEn: "Module 1 — Governance",
    bodyEs:
      "Convoque reuniones de Junta y Asambleas con agenda estructurada. La Secretaria redacta el borrador del acta, la Junta comenta y aprueba en línea, y el sistema genera el PDF y lo distribuye automáticamente a todos los titulares.",
    bodyEn:
      "Schedule Board meetings and Assemblies with structured agendas. The Secretary drafts the minutes, the Board comments and approves online, and the system generates the PDF and automatically distributes it to all owners.",
    highlightsEs: [
      "Agenda + minutos + comentarios inline",
      "Resoluciones con votación SÍ/NO/ABSTENCIÓN",
      "PDF generado automáticamente al aprobar",
    ],
    highlightsEn: [
      "Agenda + minutes + inline comments",
      "Resolutions with YES/NO/ABSTAIN voting",
      "PDF auto-generated upon approval",
    ],
    illustration: "governance",
    accent: "from-blue-50 to-blue-100",
  },
  {
    icon: <DollarSign size={28} />,
    titleEs: "Módulo 2 — Finanzas",
    titleEn: "Module 2 — Finance",
    bodyEs:
      "Genere cuotas mensuales para todas las unidades con un clic, registre pagos con método y referencia, apruebe gastos y suba informes financieros. El tablero muestra tasa de cobro, gastos por categoría y presupuesto vs real.",
    bodyEn:
      "Generate monthly fees for all units with one click, record payments with method and reference, approve expenses, and upload financial reports. The dashboard shows collection rate, expenses by category, and budget vs actual.",
    highlightsEs: [
      "Generación masiva de cuotas mensuales",
      "Recordatorios automáticos en los días y la hora que elija (Finanzas → Recordatorios)",
      "Tablero financiero con gráficos en vivo",
    ],
    highlightsEn: [
      "Bulk monthly fee generation",
      "Automatic reminders on the days and at the hour you choose (Finance → Reminders)",
      "Live finance dashboard with charts",
    ],
    illustration: "finance",
    accent: "from-green-50 to-green-100",
  },
  {
    icon: <Wrench size={28} />,
    titleEs: "Módulo 3 — Autoservicio del Residente",
    titleEn: "Module 3 — Resident Self-Service",
    bodyEs:
      "Los titulares solicitan permisos de obra, reportan problemas de mantenimiento con fotos, ven sus comunicados, descargan documentos y consultan su estado de cuenta — todo desde el portal sin llamar a la administración.",
    bodyEn:
      "Owners submit works permits, report maintenance issues with photos, see announcements, download documents, and check their account status — all from the portal without calling administration.",
    highlightsEs: [
      "Solicitudes de Titulares con flujo de aprobación automatizado",
      "Cronología visual del estado de cada solicitud",
      "Comunicados con distribución por correo a todos los titulares",
    ],
    highlightsEn: [
      "Works permits with automated approval flow",
      "Visual timeline of each request's status",
      "Announcements with email distribution to all owners",
    ],
    illustration: "resident",
    accent: "from-orange-50 to-orange-100",
  },
  {
    icon: <Shield size={28} />,
    titleEs: "Módulo 4 — Seguridad e Incidentes",
    titleEn: "Module 4 — Security & Incidents",
    bodyEs:
      "Documente incidentes con ubicación, fotos y unidad involucrada. Lleve un registro de todas las llaves del edificio: entrega, devolución y reporte de pérdidas. Controle quién tiene acceso al sistema de cámaras.",
    bodyEn:
      "Document incidents with location, photos, and involved unit. Maintain a registry of all building keys: check-out, check-in, and lost reporting. Control who has access to the camera system.",
    highlightsEs: [
      "Reporte de incidentes con flujo investigativo",
      "Registro de llaves con historial completo",
      "Control granular de acceso a cámaras",
    ],
    highlightsEn: [
      "Incident reporting with investigative flow",
      "Key registry with complete history",
      "Granular camera access control",
    ],
    illustration: "security",
    accent: "from-red-50 to-red-100",
  },
  {
    icon: <ScrollText size={28} />,
    titleEs: "Módulo 5 — Personal",
    titleEn: "Module 5 — Staff",
    bodyEs:
      "Administre al personal del condominio: mantenimiento, limpieza, seguridad. Registre horas trabajadas con entrada y salida, calcule horas automáticamente y asigne tareas en un tablero kanban con prioridades.",
    bodyEn:
      "Manage the condominium staff: maintenance, cleaning, security. Log hours with clock-in and clock-out, auto-calculate hours, and assign tasks on a kanban board with priorities.",
    highlightsEs: [
      "Hojas de tiempo con cálculo automático de horas",
      "Tablero kanban de tareas (Pendiente / En Proceso / Completada)",
      "Historial completo por empleado",
    ],
    highlightsEn: [
      "Timesheets with automatic hour calculation",
      "Kanban task board (Pending / In Progress / Done)",
      "Complete per-employee history",
    ],
    illustration: "hr",
    accent: "from-purple-50 to-purple-100",
  },
  {
    icon: <FileText size={28} />,
    titleEs: "Módulo 6 — Legal y Documentos",
    titleEn: "Module 6 — Legal & Documents",
    bodyEs:
      "Biblioteca centralizada con versionado automático del Reglamento, Manual de Normas, contratos y actas. Alertas de renovación con colores: rojo (<30d), ámbar (<60d), amarillo (<90d). Tablero de acciones legales y registro de auditoría.",
    bodyEn:
      "Centralized library with automatic versioning of the Bylaws, Rules Manual, contracts, and minutes. Color-coded renewal alerts: red (<30d), amber (<60d), yellow (<90d). Legal actions board and audit log.",
    highlightsEs: [
      "Versionado automático: subir nueva versión archiva la anterior",
      "Alertas automáticas de renovación de contratos",
      "Registro de auditoría con filtros y paginación",
    ],
    highlightsEn: [
      "Auto-versioning: new uploads archive the previous",
      "Automatic contract renewal alerts",
      "Audit log with filters and pagination",
    ],
    illustration: "legal",
    accent: "from-amber-50 to-amber-100",
  },
  {
    icon: <CheckCircle2 size={28} />,
    titleEs: "¡Está Listo!",
    titleEn: "You're All Set!",
    bodyEs:
      "Ha completado el tour. Para profundizar en una operación específica, visite Operaciones Típicas. Para detalles de cada pantalla, consulte la Guía Pantalla por Pantalla. ¿Preguntas? Contacte a la administración.",
    bodyEn:
      "You've completed the tour. To dive deeper into a specific operation, visit Typical Operations. For details on each screen, consult the Screen-by-Screen Guide. Questions? Contact administration.",
    highlightsEs: [
      "6 módulos integrados",
      "Bilingüe ES/EN con cambio instantáneo",
      "Notificaciones automáticas por correo",
    ],
    highlightsEn: [
      "6 integrated modules",
      "Bilingual ES/EN with instant switching",
      "Automatic email notifications",
    ],
    illustration: "complete",
    accent: "from-green-50 to-emerald-100",
  },
];

const AUTO_ADVANCE_MS = 7000;

export function HelpTourPage() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const isEn = i18n.language.startsWith("en");
  const t = (es: string, en: string) => (isEn ? en : es);
  const exitTo = user ? "/dashboard" : "/";
  const finishTo = user ? "/help" : "/login";

  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!playing) return;
    setProgress(0);
    const start = Date.now();
    const tick = setInterval(() => {
      const pct = ((Date.now() - start) / AUTO_ADVANCE_MS) * 100;
      if (pct >= 100) {
        setStep((s) => (s < STEPS.length - 1 ? s + 1 : s));
        if (step >= STEPS.length - 1) setPlaying(false);
      } else {
        setProgress(pct);
      }
    }, 100);
    timerRef.current = tick;
    return () => clearInterval(tick);
  }, [step, playing]);

  const current = STEPS[step];
  const highlights = isEn ? current.highlightsEn : current.highlightsEs;

  function go(delta: number) {
    const next = Math.max(0, Math.min(STEPS.length - 1, step + delta));
    setStep(next);
    setProgress(0);
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <Link to="/help" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
          <ArrowLeft size={14} /> {t("Centro de Ayuda", "Help Center")}
        </Link>
        <Link to={exitTo} className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
          <X size={14} /> {t("Saltar tour", "Skip tour")}
        </Link>
      </div>

      <div className="card overflow-hidden p-0">
        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <div className="h-full bg-primary transition-all duration-100" style={{ width: `${((step + (playing ? progress / 100 : 0)) / STEPS.length) * 100}%` }} />
        </div>

        <div className={`bg-gradient-to-br ${current.accent} p-8`}>
          <div className="flex flex-col md:flex-row gap-8 items-center">
            {/* Illustration */}
            <div className="flex-1 flex justify-center">
              <TourIllustration kind={current.illustration} />
            </div>

            {/* Content */}
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 mb-3 text-sm font-medium text-slate-600">
                <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center">{current.icon}</div>
                <span>{t("Paso", "Step")} {step + 1} {t("de", "of")} {STEPS.length}</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3 text-slate-900">
                {isEn ? current.titleEn : current.titleEs}
              </h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                {isEn ? current.bodyEn : current.bodyEs}
              </p>
              <ul className="space-y-2">
                {highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 size={16} className="text-green-600 shrink-0 mt-0.5" />
                    <span className="text-slate-700">{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => { setStep(i); setProgress(0); }}
                className={`h-2 rounded-full transition-all ${i === step ? "bg-primary w-8" : "bg-slate-300 w-2 hover:bg-slate-400"}`}
                aria-label={`${t("Ir al paso", "Go to step")} ${i + 1}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPlaying((p) => !p)}
              className="btn-secondary text-xs"
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? <><Pause size={14} /> {t("Pausar", "Pause")}</> : <><Play size={14} /> {t("Reproducir", "Play")}</>}
            </button>
            <button onClick={() => go(-1)} disabled={step === 0} className="btn-secondary text-xs disabled:opacity-50">
              <ArrowLeft size={14} />
            </button>
            {step < STEPS.length - 1 ? (
              <button onClick={() => go(1)} className="btn-primary text-xs">
                {t("Siguiente", "Next")} <ArrowRight size={14} />
              </button>
            ) : (
              <Link to={finishTo} className="btn-primary text-xs">
                {user ? t("Finalizar", "Finish") : t("Iniciar Sesión", "Sign In")} <CheckCircle2 size={14} />
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 text-center text-sm text-slate-500">
        {t(
          "También disponible: Operaciones Típicas y Guía Pantalla por Pantalla",
          "Also available: Typical Operations and Screen-by-Screen Guide"
        )}{" "}
        ·{" "}
        <Link to="/help/operations" className="text-primary hover:underline">{t("Operaciones", "Operations")}</Link>
        {" · "}
        <Link to="/help/screens" className="text-primary hover:underline">{t("Pantallas", "Screens")}</Link>
      </div>
    </div>
  );
}
